import {
    alertQueue,
    AlertType,
    Button,
    Divider,
    Flex,
    Input,
    SelectContent,
    SelectItem,
    SelectItemText,
    SelectRoot,
    SelectTriggerButton,
    SelectViewport,
    StyledTypography
} from "@connectme2/ui";
import { QueuesHelp } from "@connectme2/ui/illustrations";
import { useState } from "react";
import { DstnyForDevelopersIcon } from "../components/icons";
import RegisterService from "../services/RegisterService";
// Import validation utilities (pure, no React dependencies)
import { validateRegistrationData, RegistrationFormData, ValidationErrors, EMPTY_ERRORS } from './registrationValidation';


interface RegisterProps {
    onSubmit: () => void;
}

function RegisterFrom({ onSubmit }: RegisterProps) {
    const [formData, setFormData] = useState<RegistrationFormData>({
        email: "",
        tenant: "",
        first_name: "",
        last_name: "",
        password: "",
        repeatPassword: "",
        hp: "",
        locale: "en_US", // default language
    });
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(EMPTY_ERRORS());

    const hasAnyErrors = (errors: ValidationErrors): boolean =>
        Object.values(errors).some(arr => arr.length > 0);

    const fieldHasError = (field: keyof ValidationErrors): boolean =>
        validationErrors[field].length > 0;

    const renderErrors = (field: keyof ValidationErrors): JSX.Element[] =>
        validationErrors[field]
            .map((msg, i) => (<div key={`${field}-${i}`} className="field-error">{msg}</div>));

    const handleChange = (
        e: any
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        const registerService = new RegisterService();
        e.preventDefault();

        // Build a fresh list of validation errors each submit attempt using pure function
        const nextErrors = validateRegistrationData(formData);
        setValidationErrors(nextErrors);

        if (hasAnyErrors(nextErrors)) {
            return;
        }

        // Send information
        const { repeatPassword, hp, ...userToCreate } = formData;
        try {
            await registerService.register(userToCreate);
            onSubmit();
        } catch (e) {
            console.error(e);
            alertQueue.add({
                type: AlertType.error,
                title: "Error to submit your registration",
                description: e?.toString(),
                hasClose: true
            });
        }

        // Redirect
        // todo move it

    };

    // locale options, sorted by label
    const LOCALES = [
        { value: "de_BE", label: "German (Belgium)" },
        { value: "de_DE", label: "German (Germany)" },
        { value: "en_GB", label: "English (UK)" },
        { value: "en_US", label: "English (US)" },
        { value: "fr_BE", label: "French (Belgium)" },
        { value: "fr_FR", label: "French (France)" },
        { value: "nl_BE", label: "Dutch (Belgium)" },
        { value: "nl_NL", label: "Dutch (Netherlands)" },
    ].sort((a, b) => a.label.localeCompare(b.label));

    const [localState, setLocalState] = useState({ value: "en_US", label: "English (US)" });
    const [isOpen, setIsOpen] = useState<boolean>(false);


    return <form onSubmit={handleSubmit} className="register-form">
        <Flex direction="column" gap="lg">
            <Flex direction="row" gap="lg">
                <div className="form-row">
                    <label htmlFor="first_name">First name</label>
                    <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        variant={fieldHasError('first_name') ? 'error' : 'normal'}
                    />
                    {renderErrors('first_name')}
                </div>

                <div className="form-row">
                    <label htmlFor="last_name">Last name</label>
                    <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        variant={fieldHasError('last_name') ? 'error' : 'normal'}
                    />
                    {renderErrors('last_name')}
                </div>
            </Flex>

            <div className="form-row">
                <label htmlFor="locale">Language</label>
                <SelectRoot open={isOpen} defaultValue={localState.value} block>
                    <SelectTriggerButton onClick={() => {
                        setIsOpen(!isOpen);
                    }} block>
                        <StyledTypography>{localState.label}</StyledTypography>
                    </SelectTriggerButton>
                    <SelectContent>
                        <SelectViewport isOpen={isOpen} alignRight block>
                            {LOCALES.map((option, index) => (
                                <SelectItem
                                    key={`${option.value}-${index}`}
                                    value={option.value}
                                    onClick={() => {
                                        handleChange({ target: { name: "locale", value: option.value } });
                                        setLocalState(option);
                                        setIsOpen(false);
                                    }}
                                    option={option.value}
                                    index={index}
                                >
                                    <SelectItemText>{option.label}</SelectItemText>
                                </SelectItem>
                            ))}
                        </SelectViewport>
                    </SelectContent>
                </SelectRoot>
                {renderErrors('locale')}
            </div>

            <Divider hasFullWidth />
            <div className="form-row">
                <label htmlFor="email">Email</label>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant={fieldHasError('email') ? 'error' : 'normal'}
                />
                {renderErrors('email')}
            </div>

            <div className="form-row">
                <label htmlFor="password">Password</label>
                <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    variant={fieldHasError('password') ? 'error' : 'normal'}
                />
                {renderErrors('password')}
            </div>

            <div className="form-row">
                <label htmlFor="repeatPassword">Repeat Password</label>
                <Input
                    id="repeatPassword"
                    name="repeatPassword"
                    type="password"
                    value={formData.repeatPassword}
                    onChange={handleChange}
                    required
                    variant={fieldHasError('repeatPassword') ? 'error' : 'normal'}
                />
                {renderErrors('repeatPassword')}
            </div>

            <Divider hasFullWidth />

            <div className="form-row">
                <label htmlFor="tenant">Company / department</label>
                <Input
                    id="tenant"
                    name="tenant"
                    value={formData.tenant}
                    onChange={handleChange}
                    required
                    variant={fieldHasError('tenant') ? 'error' : 'normal'}
                />
                {renderErrors('tenant')}
            </div>

        </Flex>
        <div className="form-row hp">
            <label htmlFor="hp">hp</label>
            <Input
                id="hp"
                name="hp"
                type="text"
                value={formData.hp}
                onChange={handleChange}
            />
        </div>

        <div className="form-row">
            <Button type="submit" style={{ margin: "20px auto" }}>Register</Button>
        </div>
    </form>
}

function RegisterSubmitted() {
    return <div>
        <QueuesHelp />
        <p>Please check your email, we have sent you a verification message to confirm your identity</p>
    </div>
}

function RegisterPage() {

    const [isSubmitted, submitForm] = useState(false);

    return <div className="app-register">
        <DstnyForDevelopersIcon />
        <Flex direction="column" alignItems="center" >
            {!isSubmitted ?
                <>
                    <h2>Register Dstny developer account</h2>
                    <RegisterFrom
                        onSubmit={() => {
                            submitForm(true);
                        }}
                    />
                </>
                :
                <RegisterSubmitted />
            }
        </Flex>
    </div>
}

export default RegisterPage;
